// HEAL-47 — граница ошибок. До неё в приложении не было НИ ОДНОГО ErrorBoundary.
//
// Что это значило на практике: любое исключение при отрисовке — одна кривая точка,
// неожиданный null в маркере — сносило всё дерево React, и пользователь получал
// пустой белый экран без единого слова. Это ровно та картина, что пришла в тикете:
// баг привязан к аккаунту (дело в данных, а не в устройстве), переустановка не
// помогает (данные лежат на сервере), телефон греется (react-query продолжает
// крутить поллинг и retry, пока пользователь перезапускает приложение по кругу).
//
// Граница ставится в двух ролях:
//   1. корневая — вместо белого экрана показывает, что случилось, и даёт выход;
//   2. точечная (`ItemErrorBoundary`) — вокруг одного элемента списка, чтобы один
//      сломанный показатель не уносил весь экран. Требование тикета дословно:
//      «если один анализ или показатель вызывает ошибку, приложение должно
//      пропустить проблемный объект и показать интерфейс».

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from '@/lib/client-error-report';

type Props = {
  children: ReactNode;
  /** Где рвануло — попадёт в лог, помогает искать по жалобе «у меня белый экран». */
  scope: string;
  /** Что показать вместо упавшего поддерева. Получает функцию сброса. */
  fallback: (reset: () => void) => ReactNode;
};

type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Логируем ВСЕГДА, даже если отправка на сервер не дойдёт: консоль остаётся
    // единственным следом при отладке на устройстве через Safari Web Inspector.
    console.error(`[${this.props.scope}]`, error, info.componentStack);
    reportClientError({
      scope: this.props.scope,
      message: error.message,
      stack: error.stack ?? null,
      componentStack: info.componentStack ?? null,
    });
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) return this.props.fallback(this.reset);
    return this.props.children;
  }
}

/**
 * Обёртка вокруг одного элемента списка: элемент падает — на его месте остаётся
 * скромная плашка, остальной экран живёт. Молча прятать нельзя: пользователь
 * должен видеть, что показатель есть, но мы его не смогли отрисовать.
 */
export function ItemErrorBoundary({ scope, children }: { scope: string; children: ReactNode }) {
  return (
    <ErrorBoundary
      scope={scope}
      fallback={() => (
        <div className="rounded-surface border border-border bg-muted p-3 text-hl-small text-muted-foreground">
          Не удалось показать этот показатель. Остальные данные в порядке.
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
